import {
  Property,
  County,
  Lead,
  NoticeLead,
  BuyBox,
  BuyBoxEmailFrequency,
  BuyBoxInput,
  LockedProperty,
  MapPoint,
  PropertyRow,
  isLockedProperty,
} from "./types";
import { apiClient } from "./lib/apiClient";
import { TrialExportLockedError } from "./lib/exportErrors";
import { takeEarlyCounties } from "./lib/earlyFetch";

export const DEFAULT_COUNTY_ID = "mecklenburg-nc";

// LeadFilters is the buy-box criteria set the Leads table filters on. Everything
// beyond countyId/absentee/limit maps to the server-side buy-box filters added in
// migration 054 / leads_repository.buildLeadsQuery.
export interface LeadFilters {
  countyId?: string;
  counties?: string[]; // multi-county; takes precedence over countyId server-side
  signalTypes?: string[];
  propertyClasses?: string[];
  minValue?: number | null;
  maxValue?: number | null;
  minEquity?: number | null;
  minAcres?: number | null;
  maxAcres?: number | null;
  // Amount owed (unpaid taxes or the debt figure) and land value, in dollars.
  minAmountDue?: number | null;
  maxAmountDue?: number | null;
  minLandValue?: number | null;
  maxLandValue?: number | null;
  absentee?: boolean;
  entity?: boolean;
  heir?: boolean;
  // highConfidenceOnly (#2140): restrict to leads carrying BOTH an active
  // foreclosure status AND a code-violation-family signal — see
  // repositories.LeadFilters.HighConfidenceOnly.
  highConfidenceOnly?: boolean;
  // Building criteria (#3061). includeUnknownBuilding defaults to true on the
  // server; only an explicit false is sent.
  minSqft?: number | null;
  maxSqft?: number | null;
  minYearBuilt?: number | null;
  maxYearBuilt?: number | null;
  minBeds?: number | null;
  minBaths?: number | null;
  includeUnknownBuilding?: boolean;
  // RFC 3339: only properties first ingested after this instant (the export's
  // "new since my last export").
  createdAfter?: string;
  limit?: number;
}

function leadParams(f?: LeadFilters): Record<string, string> {
  const params: Record<string, string> = {};
  if (!f) return params;
  if (f.countyId) params.countyId = f.countyId;
  if (f.counties && f.counties.length) params.counties = f.counties.join(",");
  if (f.signalTypes && f.signalTypes.length) params.signalTypes = f.signalTypes.join(",");
  if (f.propertyClasses && f.propertyClasses.length) params.propertyClasses = f.propertyClasses.join(",");
  if (f.minValue != null) params.minValue = String(f.minValue);
  if (f.maxValue != null) params.maxValue = String(f.maxValue);
  if (f.minEquity != null) params.minEquity = String(f.minEquity);
  if (f.minAcres != null) params.minAcres = String(f.minAcres);
  if (f.maxAcres != null) params.maxAcres = String(f.maxAcres);
  if (f.minAmountDue != null) params.minAmountDue = String(f.minAmountDue);
  if (f.maxAmountDue != null) params.maxAmountDue = String(f.maxAmountDue);
  if (f.minLandValue != null) params.minLandValue = String(f.minLandValue);
  if (f.maxLandValue != null) params.maxLandValue = String(f.maxLandValue);
  if (f.absentee) params.absentee = "true";
  if (f.entity) params.entity = "true";
  if (f.heir) params.heir = "true";
  if (f.highConfidenceOnly) params.highConfidenceOnly = "true";
  if (f.minSqft != null) params.minSqft = String(f.minSqft);
  if (f.maxSqft != null) params.maxSqft = String(f.maxSqft);
  if (f.minYearBuilt != null) params.minYearBuilt = String(f.minYearBuilt);
  if (f.maxYearBuilt != null) params.maxYearBuilt = String(f.maxYearBuilt);
  if (f.minBeds != null) params.minBeds = String(f.minBeds);
  if (f.minBaths != null) params.minBaths = String(f.minBaths);
  if (f.includeUnknownBuilding === false) params.includeUnknownBuilding = "false";
  if (f.createdAfter) params.createdAfter = f.createdAfter;
  if (f.limit) params.limit = String(f.limit);
  return params;
}

export interface LeadsResponse {
  leads: Lead[];
  totalCount: number;
}

// getLeads fetches the Pro-gated pre-foreclosure seller-leads list. A 403
// means the user's plan lacks access — callers should surface the upgrade
// prompt rather than an error.
export async function getLeads(filters?: LeadFilters): Promise<LeadsResponse> {
  const resp = await apiClient.get<Lead[]>("/leads", { params: leadParams(filters) });
  return {
    leads: resp.data || [],
    totalCount: parseInt(resp.headers["x-total-count"] || "0", 10),
  };
}

// ── CSV exports ─────────────────────────────────────────────────────────────
// A paid plan on a free trial that hasn't paid yet gets 403
// {"error":"trial_export_locked"} from every export (migration 240). The body
// of a blob request arrives as a Blob, so getExportBlob reads it and throws
// TrialExportLockedError; callers turn any error into copy with
// exportErrorMessage (lib/exportErrors).

function readBlobText(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

async function getExportBlob(url: string, params?: unknown): Promise<Blob> {
  try {
    const resp = await apiClient.get(url, { params, responseType: "blob" });
    return resp.data as Blob;
  } catch (err: any) {
    const data = err?.response?.data;
    if (err?.response?.status === 403 && data instanceof Blob) {
      const body = await readBlobText(data).catch(() => "");
      if (body.includes('"trial_export_locked"')) throw new TrialExportLockedError();
    }
    throw err;
  }
}


// downloadLeadsCSV fetches the CSV export as a Blob (auth header is attached
// by the apiClient interceptor — a plain <a download> couldn't carry it).
export async function downloadLeadsCSV(filters?: LeadFilters): Promise<Blob> {
  return getExportBlob("/leads/export", leadParams(filters));
}

// ── Buy boxes (saved lead-filter sets) ──────────────────────────────────────
// All Pro-gated; a 403 means the plan lacks access (surface the upgrade prompt).
// The backend wraps payloads in { data: ... }.

export async function getBuyBoxes(): Promise<BuyBox[]> {
  const resp = await apiClient.get<{ data: BuyBox[] }>("/buy-boxes");
  return resp.data?.data || [];
}

export async function createBuyBox(input: BuyBoxInput): Promise<BuyBox> {
  const resp = await apiClient.post<{ data: BuyBox }>("/buy-boxes", input);
  return resp.data.data;
}

export async function updateBuyBox(id: string, input: BuyBoxInput): Promise<BuyBox> {
  const resp = await apiClient.put<{ data: BuyBox }>(`/buy-boxes/${id}`, input);
  return resp.data.data;
}

// Set one saved box's email alert cadence (#3062). Separate from updateBuyBox,
// whose full replace would otherwise reset it.
export async function setBuyBoxEmail(id: string, frequency: BuyBoxEmailFrequency): Promise<void> {
  await apiClient.put(`/buy-boxes/${id}/email`, { frequency });
}

export async function deleteBuyBox(id: string): Promise<void> {
  await apiClient.delete(`/buy-boxes/${id}`);
}

// The statewide legal-notice record types (notice_leads). "" = all types.
export type NoticeRecordType =
  | "probate"
  | "foreclosure"
  | "hoa_foreclosure"
  | "divorce"
  | "tax_foreclosure"
  | "execution_sale";

export interface NoticeLeadFilters {
  state?: string;
  countyId?: string;
  recordType?: NoticeRecordType | "";
  limit?: number;
  // #2214: server-side filter requiring owner_name AND (situs OR mailing
  // address). 41% of notice_leads rows have neither and render as a bare "—"
  // row with nothing to act on. Callers default it ON; there is no UI toggle.
  directMailOnly?: boolean;
}

function noticeLeadParams(f?: NoticeLeadFilters): Record<string, string> {
  const params: Record<string, string> = {};
  if (f?.state) params.state = f.state;
  if (f?.countyId) params.countyId = f.countyId;
  if (f?.recordType) params.recordType = f.recordType;
  if (f?.limit) params.limit = String(f.limit);
  if (f?.directMailOnly) params.directMailOnly = "true";
  return params;
}

export interface NoticeLeadsResponse {
  noticeLeads: NoticeLead[];
  totalCount: number;
}

// getNoticeLeads fetches the Pro-gated statewide legal-notice leads. A 403 means
// the plan lacks access — callers surface the same upgrade prompt as getLeads.
export async function getNoticeLeads(filters?: NoticeLeadFilters): Promise<NoticeLeadsResponse> {
  const resp = await apiClient.get<NoticeLead[]>("/notice-leads", { params: noticeLeadParams(filters) });
  return {
    noticeLeads: resp.data || [],
    totalCount: parseInt(resp.headers["x-total-count"] || "0", 10),
  };
}

// downloadNoticeLeadsCSV fetches the notice-leads CSV export as a Blob (auth
// header attached by the apiClient interceptor).
export async function downloadNoticeLeadsCSV(filters?: NoticeLeadFilters): Promise<Blob> {
  return getExportBlob("/notice-leads/export", noticeLeadParams(filters));
}

// Bidder mirrors the backend contract returned by
// GET /properties/:id/bidders — an upset bidder / purchaser captured from
// public court record (name + role + address). Public data, not Pro-gated.
export interface Bidder {
  name: string;
  role: string;
  address: string;
}

// getPropertyBidders fetches the upset-bidder / purchaser list for a property
// (GET /properties/:id/bidders — public via OptionalAuth). Returns [] when none
// are on file (most properties). Callers should hide the panel on empty.
export async function getPropertyBidders(propertyId: string): Promise<Bidder[]> {
  const { data } = await apiClient.get<Bidder[]>(`/properties/${propertyId}/bidders`);
  return data || [];
}

// downloadBuyerListCSV fetches the deduped buyer-list CSV export as a Blob
// (auth header is attached by the apiClient interceptor — a plain <a download>
// couldn't carry it). Pro-gated: a 403 throws so callers can surface the
// upgrade prompt, exactly like downloadLeadsCSV.
export async function downloadBuyerListCSV(): Promise<Blob> {
  return getExportBlob("/bidders/export");
}

// downloadPropertiesCSV fetches a county's property feed as a CSV Blob
// (GET /properties/export: owner + mailing, lead types, first-added date).
// Scout and up — a 403 throws so callers can surface the upgrade prompt, like
// the other exports. `addedWithinDays` keeps only rows Parcely first added in
// the last N days ("what's new since yesterday" for a daily CRM pull).
export async function downloadPropertiesCSV(
  countyId: string,
  addedWithinDays?: number,
  // Bulk select (#3079): export just these properties (at most 500).
  ids?: string[]
): Promise<Blob> {
  return getExportBlob("/properties/export", {
    countyId,
    ...(addedWithinDays ? { addedWithinDays } : {}),
    ...(ids && ids.length ? { ids: ids.join(",") } : {}),
  });
}

export interface PropertiesResponse {
  /** Fully-readable rows. Locked rows are split out below. */
  properties: Property[];
  /**
   * Rows the viewer's plan doesn't unlock. Carries teaser fields and
   * deliberately-offset coordinates — enough to render a grey dot and a
   * per-zip count, not enough to act on.
   */
  lockedProperties: LockedProperty[];
  totalCount: number;
  /**
   * The UNLOCKED count — the X in "X of Y unlocked". Named `freeTierLimit`
   * for continuity with the header it comes from.
   */
  freeTierLimit: number;
}

export async function getProperties(
  countyId: string = DEFAULT_COUNTY_ID
): Promise<PropertiesResponse> {
  // The wire format stays a bare array (no envelope) — it's load-bearing
  // across tests and fixtures, and the one new aggregate the UI needs
  // (per-zip locked counts) is derivable client-side from the locked rows.
  const resp = await apiClient.get<PropertyRow[]>("/properties", {
    params: { countyId },
  });
  const totalCount = parseInt(resp.headers["x-total-count"] || "0", 10);
  const freeTierLimit = parseInt(resp.headers["x-free-tier-limit"] || "0", 10);

  // Split at the API boundary, once. Everything downstream — filters, sort,
  // the list, the watchlist, PropertyCard — keeps operating on Property[]
  // with no changes at all, and can't accidentally treat a locked row as a
  // real one.
  const rows = resp.data || [];
  const properties: Property[] = [];
  const lockedProperties: LockedProperty[] = [];
  for (const row of rows) {
    if (isLockedProperty(row)) lockedProperties.push(row);
    else properties.push(row);
  }

  return { properties, lockedProperties, totalCount, freeTierLimit };
}

export interface MapPointsResponse {
  /** Unlocked rows as map pins. NOT Property rows — no address, value or owner. */
  points: MapPoint[];
  /** Identical to PropertiesResponse.lockedProperties for the same request. */
  lockedProperties: LockedProperty[];
  totalCount: number;
  freeTierLimit: number;
}

/**
 * getPropertyMapPoints fetches the slim map payload
 * (GET /properties?countyId=…&fields=map).
 *
 * Same gating, county scope, status set and count headers as getProperties —
 * the server runs one code path — but each unlocked row is a MapPoint and the
 * server skips the photo/owner/lead-signal fills, so it answers in a fraction
 * of the time. The dashboard draws the map from it while the full list is
 * still loading; it is never a replacement for getProperties.
 */
export async function getPropertyMapPoints(
  countyId: string = DEFAULT_COUNTY_ID
): Promise<MapPointsResponse> {
  const resp = await apiClient.get<Array<MapPoint | LockedProperty>>("/properties", {
    params: { countyId, fields: "map" },
  });
  const rows = resp.data || [];
  const totalCount = parseInt(resp.headers["x-total-count"] || "0", 10);
  const freeTierLimit = parseInt(resp.headers["x-free-tier-limit"] || "0", 10);

  const points: MapPoint[] = [];
  const lockedProperties: LockedProperty[] = [];
  for (const row of rows) {
    if ((row as LockedProperty).locked === true) lockedProperties.push(row as LockedProperty);
    else points.push(row as MapPoint);
  }
  return { points, lockedProperties, totalCount, freeTierLimit };
}

// Returns PropertyRow, not Property: GET /properties/:id deliberately NEVER
// 403s. When the plan gate is closed the handler returns a LockedProperty
// teaser instead (property_handler.go's lockProperty), so a shared link, a
// deep link (#556/#557) or a cross-county search hit lands on an upgrade
// prompt rather than a dead end.
//
// This was typed as Promise<Property> (#1872), so both direct-fetch callers
// pushed a locked teaser into Property-typed state with no guard — the
// deep-link and map-click paths. Narrow with isLockedProperty at the call
// site; the list path at getProperties already does.
export async function getProperty(id: string): Promise<PropertyRow> {
  const { data } = await apiClient.get<PropertyRow>(`/properties/${id}`);
  return data;
}

/**
 * searchProperties hits POST /properties/search with a text query.
 *
 * `countyId` semantics (#244 cross-county search):
 *   - undefined / empty string → backend searches across ALL active
 *     counties. Each result carries its own countyId so the caller
 *     can detect "this is in a different county" and switch.
 *   - explicit countyId → backend scopes to that county only.
 *
 * `limit` defaults to backend's 16 if not supplied.
 *
 * Backwards compat: callers passing the old positional countyId still
 * work; we just send the same field through. Existing api.test.ts
 * cases still pass — the field name on the wire is unchanged.
 */
// Returns PropertyRow, not Property[] (#2378): the backend's own
// projectSearchResults (property_handler.go) locks any row outside the
// viewer's free-tier subset — a search hit is exactly what free_subset.go's
// own comment calls "the sharpest bypass risk in the whole model" if left
// ungated, so it returns a LockedProperty teaser instead of a real row for
// those. This was typed Promise<Property[]> the same way getProperty was
// before #1872 fixed it — pushing a locked teaser into Property-typed state
// with no guard. SearchAutocomplete is the one caller; it must narrow with
// isLockedProperty per row.
export async function searchProperties(
  query: string,
  countyId?: string,
  limit?: number
): Promise<PropertyRow[]> {
  const body: { query: string; countyId?: string; limit?: number } = { query };
  if (countyId) body.countyId = countyId;
  if (limit) body.limit = limit;
  const { data } = await apiClient.post<PropertyRow[]>("/properties/search", body);
  return data;
}

// Single-flight + session cache for GET /counties. Measured 2026-10-09: the
// signed-in app issued this request TWICE at the same instant (Dashboard and
// CountySelector both call it on mount, ~1.1 s each), and CountyPickerModal /
// AlertSettings add more when opened. The promise is shared by every caller;
// a rejection clears it so the next caller retries instead of caching the
// failure. resetCountiesCache() is called on sign-out (Dashboard).
let countiesInFlight: Promise<County[]> | null = null;

export function getCounties(): Promise<County[]> {
  if (!countiesInFlight) {
    // async IIFE so a synchronous throw from apiClient.get becomes a rejection,
    // exactly as when this was an `async function`.
    // The inline script in public/index.html may already have this request in
    // flight (lib/earlyFetch.ts). /counties is identical for every caller, so
    // it is consumed regardless of auth; any failure or odd shape falls
    // through to the normal request below.
    const early = takeEarlyCounties();
    const p = (async () => {
      if (early) {
        const r = await early;
        if (r && Array.isArray(r.data)) return r.data as County[];
      }
      const { data } = await apiClient.get<County[]>("/counties");
      return data;
    })();
    countiesInFlight = p;
    p.catch(() => {
      if (countiesInFlight === p) countiesInFlight = null;
    });
  }
  return countiesInFlight;
}

// ── Public county stats (single-flight) ────────────────────────────────────
// The landing page mounts three hooks (useTrackedPropertiesCount,
// useAddressedLeadsCount, usePublicCountyStats) that all read the same public
// GET /public/county-stats, so a logged-out load fired it three times in the
// same millisecond (A8). Callers that overlap share one request. Unlike
// getCounties this is NOT kept after it settles: the numbers are live, so a
// later mount gets a fresh read; a failure is therefore also never cached.
let publicCountyStatsInFlight: Promise<unknown> | null = null;

export function getPublicCountyStats<T = unknown>(): Promise<T> {
  if (!publicCountyStatsInFlight) {
    const p: Promise<unknown> = (async () => {
      const { data } = await apiClient.get<unknown>("/public/county-stats");
      return data;
    })();
    publicCountyStatsInFlight = p;
    const clear = () => {
      if (publicCountyStatsInFlight === p) publicCountyStatsInFlight = null;
    };
    p.then(clear, clear);
  }
  return publicCountyStatsInFlight as Promise<T>;
}

export function resetCountiesCache(): void {
  countiesInFlight = null;
}

// ── Profile counties (Free/Scout geography-aware tier redesign) ────────────
// Free picks 1 county, Scout up to 3; Pro/team/admin are unrestricted and
// never call these. See backend/internal/handlers/profile_counties_handler.go
// for the full cadence rules (48h correction window / 180-day change cadence).

export interface ProfileCountiesResponse {
  chosenCounties: string[];
  countiesChangedAt: string | null;
  countiesOnboardedAt: string | null;
  /** Migration 241: null until the "how did you hear about us" prompt was
   *  answered or skipped. Rides on this call to avoid a fourth profile
   *  request per sign-in. Older API builds omit it (undefined). */
  signupSourceAskedAt?: string | null;
  /** Migration 261: true for a new free account that has not finished the
   *  required onboarding questionnaire. Older API builds omit it. */
  onboardingRequired?: boolean;
  /** The zip unlocked for this user (set once by the questionnaire). */
  welcomeZip?: string | null;
}

export type SignupSource = "facebook_reia" | "google" | "letter" | "referral" | "agent" | "other" | "skipped";

/** PUT /profile/attribution — self-reported acquisition channel (one-time). */
export async function putProfileAttribution(source: SignupSource, detail = ""): Promise<{ source: SignupSource }> {
  const { data } = await apiClient.put<{ source: SignupSource }>("/profile/attribution", { source, detail });
  return data;
}

export type SignupIntent = "auction_bidder" | "wholesaler" | "flipper_landlord" | "agent" | "researching";

/** PUT /profile/attribution with only the "What are you here to do?" answer (migration 246). */
export async function putProfileIntent(intent: SignupIntent): Promise<{ intent: SignupIntent }> {
  const { data } = await apiClient.put<{ intent: SignupIntent }>("/profile/attribution", { intent });
  return data;
}

export interface OnboardingAnswers {
  intent: SignupIntent;
  experience: "none" | "1_2" | "3_10" | "10_plus";
  dealsExpected: "0_1" | "2_3" | "4_10" | "10_plus";
  challenge: "finding_leads" | "judging_deals" | "reaching_owners" | "funding" | "closing" | "time";
  tools: Array<"spreadsheets" | "paid_list_service" | "courthouse" | "agent_mls" | "nothing" | "other">;
  zip: string;
  source?: Exclude<SignupSource, "skipped">;
}

/** PUT /profile/onboarding — the required questionnaire (migration 261).
 *  Turns on one new-listing alert for `zip` and unlocks that zip. 422
 *  zip_not_in_county / 409 county_required carry a user-facing `message`. */
export async function putProfileOnboarding(answers: OnboardingAnswers): Promise<{ ok: boolean; welcomeZip: string; zipKept: boolean }> {
  const { data } = await apiClient.put<{ ok: boolean; welcomeZip: string; zipKept: boolean }>("/profile/onboarding", answers);
  return data;
}

export type SetupCallBestTime = "weekday_morning" | "weekday_afternoon" | "weekday_evening" | "weekend" | "any";

export interface SetupCallRequest {
  phone: string;
  phoneConsent: boolean;
  bestTime: SetupCallBestTime;
  note: string;
  source: string;
}

/** POST /setup-call-requests — "Book a 15-minute setup call". 400s carry a
 *  user-facing `message` (bad phone, phone without consent). */
export async function postSetupCallRequest(req: SetupCallRequest): Promise<{ ok: boolean }> {
  const { data } = await apiClient.post<{ ok: boolean }>("/setup-call-requests", req);
  return data;
}

export async function getProfileCounties(): Promise<ProfileCountiesResponse> {
  const { data } = await apiClient.get<ProfileCountiesResponse>("/profile/counties");
  return data;
}

// putProfileCounties throws on 400 (too_many_counties / invalid_county_id /
// plan_not_eligible_for_county_selection) or 403 (county_change_not_yet_eligible)
// — callers should catch and read err.response.data for the real message,
// same pattern as every other Pro-gated call in this file.
export async function putProfileCounties(counties: string[]): Promise<{ chosenCounties: string[] }> {
  const { data } = await apiClient.put<{ chosenCounties: string[] }>("/profile/counties", { counties });
  return data;
}

// markCountiesOnboarded idempotently stamps that the county picker has been
// shown/dismissed, independent of whether the user actually picked a county.
export async function markCountiesOnboarded(): Promise<void> {
  await apiClient.post("/profile/counties/onboarded");
}

// ── Notification bell (#2302) ────────────────────────────────────────────
// GET /notifications/summary: count of NEW leads matching the caller's saved
// buy boxes (or chosen counties) since they last opened the bell. 403
// pro_required for Free. No lead/owner data — only counts and box names.
export interface NotificationGroup {
  boxId: string; // "" for the "Your counties" fallback group
  name: string;
  count: number;
}
export interface NotificationSummary {
  count: number;
  capped: boolean; // true = count is a floor ("100+")
  since: string;
  asOf: string;
  scope: "buy_boxes" | "counties" | "none";
  groups: NotificationGroup[];
}

export async function getNotificationSummary(): Promise<NotificationSummary> {
  const { data } = await apiClient.get<NotificationSummary>("/notifications/summary");
  return data;
}

// markNotificationsSeen advances the high-water mark to `through` (the asOf of
// the summary being acknowledged), so leads ingested after that stay unread.
export async function markNotificationsSeen(through?: string): Promise<void> {
  await apiClient.post("/notifications/seen", through ? { through } : {});
}

// ── Billing ──────────────────────────────────────────────────────────────
// GET /billing/trial-eligibility: whether checkout will give the signed-in
// user the 14-day free trial. One trial per person: anyone who has had a
// Parcely subscription before is charged at checkout, so the pricing page
// should only say "Start 14-day trial" when this is true. The server answers
// false whenever it cannot tell, matching what checkout itself will do.
export interface TrialEligibility {
  trialEligible: boolean;
}

export async function getTrialEligibility(): Promise<TrialEligibility> {
  const { data } = await apiClient.get<TrialEligibility>("/billing/trial-eligibility");
  return { trialEligible: data?.trialEligible === true };
}

// GET /billing/status: payment state for the in-app payment notice
// (PaymentIssueBanner). paymentIssue = an unpaid invoice is in dunning;
// graceUntil = when paid access stops unless the payment goes through (null
// when there is no issue); accessPaused = that moment has passed; plan = the
// plan the customer is subscribed to (the one they stand to lose).
export interface BillingStatus {
  plan: string;
  paymentIssue: boolean;
  graceUntil: string | null;
  accessPaused: boolean;
  portalAvailable: boolean;
}

// ── Calendar feed (#3063) ───────────────────────────────────────────────────
// A per-user secret URL that Google/Apple/Outlook Calendar subscribe to. The
// raw token comes back once, from createCalendarFeed; afterwards only whether
// a feed is active can be read.

export interface CalendarFeedStatus {
  active: boolean;
  createdAt: string | null;
}

export async function getCalendarFeed(): Promise<CalendarFeedStatus> {
  const { data } = await apiClient.get<Partial<CalendarFeedStatus>>("/calendar-feed");
  return { active: data?.active === true, createdAt: data?.createdAt ?? null };
}

export async function createCalendarFeed(): Promise<{ token: string; createdAt: string }> {
  const { data } = await apiClient.post<{ token: string; createdAt: string }>("/calendar-feed");
  return data;
}

export async function revokeCalendarFeed(): Promise<void> {
  await apiClient.delete("/calendar-feed");
}

/** The public https URL of a feed token, built from the API base. */
export function calendarFeedURL(token: string): string {
  const base = (apiClient.defaults.baseURL || "").replace(/\/+$/, "");
  const absolute = /^https?:\/\//i.test(base) ? base : window.location.origin + base;
  return `${absolute}/calendar/${encodeURIComponent(token)}.ics`;
}

// ── Watchlist share links (#3213) ───────────────────────────────────────────
// Revocable read-only links to the user's watchlist. The raw token comes back
// once, from createShareLink; recipients open /w/<token> with no account and
// get the trimmed SharedProperty set (address, sale date, opening bid, status,
// photo; nothing the paid tiers sell).

export interface ShareLink {
  id: string;
  label: string;
  createdAt: string;
  viewCount: number;
  lastViewedAt: string | null;
}

export interface SharedProperty {
  id: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  county: string;
  status: string;
  saleDate: string | null;
  saleDateUnconfirmed?: boolean;
  openingBid: number | null;
  primaryImage?: Property["primaryImage"];
}

export interface SharedWatchlist {
  label: string;
  properties: SharedProperty[];
}

export async function listShareLinks(): Promise<ShareLink[]> {
  const { data } = await apiClient.get<{ data?: ShareLink[] }>("/share-links");
  return data?.data ?? [];
}

export async function createShareLink(label: string): Promise<{ token: string; link: ShareLink }> {
  const { data } = await apiClient.post<{ token: string; link: ShareLink }>("/share-links", { label });
  return data;
}

export async function revokeShareLink(id: string): Promise<void> {
  await apiClient.delete(`/share-links/${encodeURIComponent(id)}`);
}

export async function getSharedWatchlist(token: string): Promise<SharedWatchlist> {
  const { data } = await apiClient.get<SharedWatchlist>(`/shared/watchlist/${encodeURIComponent(token)}`);
  return { label: data?.label ?? "", properties: data?.properties ?? [] };
}

/** The public page a recipient opens (the app's own origin, not the API's). */
export function shareLinkURL(token: string): string {
  return `${window.location.origin}/w/${encodeURIComponent(token)}`;
}

export async function getBillingStatus(): Promise<BillingStatus> {
  const { data } = await apiClient.get<Partial<BillingStatus>>("/billing/status");
  return {
    plan: data?.plan || "free",
    paymentIssue: data?.paymentIssue === true,
    graceUntil: data?.graceUntil || null,
    accessPaused: data?.accessPaused === true,
    portalAvailable: data?.portalAvailable === true,
  };
}

// POST /billing/portal: a Stripe Customer Portal session URL.
export async function openBillingPortalUrl(): Promise<string> {
  const { data } = await apiClient.post<{ url: string }>("/billing/portal");
  return data.url;
}
